// genericCaseSchema.js
import { z } from "zod";

// Helper pour transformer Map en objet simple (pour validation JSON)
const mapToObject = z.object({}).passthrough();

const optionalFlexibleYear = z.preprocess(
  (v) => (v === "" || v === null ? undefined : v),
  z.union([z.coerce.number(), z.string()]).optional()
);

// Schéma pour une interaction (addInteractions, network, etc.)
const interactionSchema = z.object({
  source: z.string().min(1),
  target: z.string().min(1),
  type: z.enum([
    'créative', 'soutien', 'conflit', 'pression', 'autorité', 'contrainte', 'stress',
    'renforcement', 'collaboration', 'influence', 'distance', 'soin', 'influenced',
    'subversion', 'exclusion', 'resistance', 'hégémonie', 'refus', 'légitimité',
    'schemaDriven', 'workflowDependency', 'polymorphicSuccess', 'pricingConflict',
    'atomicPropagation', 'documentRegulatory' // <-- Ajouté celui-ci
  ]).default('créative'),
  strength: z.coerce.number().optional(),
  evolving: z.boolean().default(true),
});

const metricDetailSchema = z.object({
  start: z.coerce.number(),
  min: z.coerce.number().default(0),
  max: z.coerce.number().default(100),
  color: z.string().optional(),
  hidden: z.boolean().default(false)
});

// Schéma pour une micro-effect
const microEffectSchema = z.object({
  target: z.string().min(1),
  variable: z.string().min(1),
  delta: z.coerce.number(),
  temporalWeight: z.coerce.number().optional(),
  delay: z.coerce.number().default(0).optional(),
});

// Schéma pour une action
const actionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  effect: z.record(z.string(), z.coerce.number()).optional(),
  prerequisites: z.union([
    z.string(),
    z.array(z.string())
  ]).transform((val) => {
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return val;
  }),
  addInteractions: z.union([
    z.string(),
    z.array(interactionSchema)
  ]).transform((val) => {
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return val;
  }).optional(),
  toggleFamilies: z.union([
    z.string(),
    z.array(z.string())
  ]).transform((val) => {
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return val;
  }),
  feedback: z.string().optional(),
  highlightFamilies: z.union([
    z.string(),
    z.array(z.string())
  ]).transform((val) => {
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return val;
  }),
  microEffects: z.union([
  z.string(),
  z.array(microEffectSchema)
]).transform((val) => {
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return val;
}).optional(),
});

// Schéma pour une phase
const phaseSchema = z.object({
  type: z.enum(['observation', 'action', 'consequences', 'tension', 'bifurcation']),
  duration: z.coerce.number().default(10),
  description: z.string().optional(),
  actionIds: z.array(z.string()).optional(),
  autoEvents: z.array(
    z.object({
      effect: z.record(z.string(), z.any()).optional(),
      description: z.string().optional(),
      probability: z.coerce.number().default(1.0),
    })
  ).optional(),
  microEffects: z.union([
  z.string(),
  z.array(microEffectSchema)
]).transform((val) => {
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return val;
}).optional(),
});

// Schéma pour un step
const stepSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  problematisation: z.string().optional(),
  epistemologicalQuestion: z.string().optional(),
  feedback: z.string().optional(),
  narrativeOverride: z.string().optional(),
  causality: z.array(
    z.object({
      cause: z.string(),
      effect: z.string(),
      weight: z.coerce.number(),
      evidence: z.object({
        source: z.string(),
        strength: z.coerce.number(),
      }).optional(),
    })
  ).optional(),
  phases: z.array(phaseSchema).optional(),
  actions: z.array(actionSchema).optional(),
  autoEvents: z.array(
    z.object({
      trigger: z.string().optional(),
      condition: z.object({
        parameter: z.string(),
        operator: z.string(),
        threshold: z.coerce.number(),
      }).optional(),
      effect: z.record(z.string(), z.any()).optional(),
      description: z.string().optional(),
    })
  ).optional(),
});

// Schéma pour une famille
const familySchema = z.object({
  name: z.string().min(1),
  influence: z.record(z.string(), z.coerce.number()).default({}),
  type: z.enum(['positive', 'neutral', 'negative', 'toxic', 'service', 'diagnostic', 'membership']).default('neutral'),
  isToxic: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

// Schéma pour une solution
const solutionSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  requiredActions: z.array(z.string()),
  difficulty: z.enum(['facile', 'moyen', 'difficile', 'expert']).default('moyen'),
  interpretation: z.object({
    shortSummary: z.string().min(1),
    theoreticalPromise: z.coerce.number(),
    realWorldProbability: z.coerce.number(),
    tone: z.enum(['tragique', 'lucide', 'esperant', 'cynique', 'radieux', 'désespéré', 'reformiste', 'chaotique', 'radicale']),
    keyInsight: z.string().min(1),
    historicalExamples: z.array(z.string()).optional(),
    counterArgument: z.string().optional(),
    pedagogicalMessage: z.string().min(1),
  }),
  expectedMetrics: z.record(z.string(), z.coerce.number()).optional(),
  theoreticalOutcome: z.string().optional(),
  probabilityRealWorld: z.coerce.number().optional(),
  probabilityOfSuccess: z.coerce.number().optional(),
  historicalExample: z.string().optional(),
});

// Schéma pour une dynamique
const dynamicSchema = z.object({
  parameter: z.string().min(1).optional(),           // ← .optional() pour tolérer vide
  formula: z.string().min(1).optional(),
  type: z.enum(['linear', 'nonlinear', 'threshold', 'logistic']).default('linear'),
  periodicity: z.coerce.number().default(1),
  description: z.string().optional(),
});

const counterfactualSchema = z.object({
  actionId: z.string(),
  actionTitle: z.string(),
  description: z.string(),
  deltaProbability: z.number(),
});

// Schéma principal : GenericCase
export const genericCaseSchema = z.object({
  slug: z.string().min(3).regex(/^[a-z0-9-]+$/, "Slug invalide (lettres minuscules, chiffres, tirets)"),
  title: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  pedagogicalGoal: z.string().min(1),
  thumbnail: z.string().optional(),
  tags: z.array(z.string()).optional(),

  module: z.string().optional(),
  metrics: z.record(z.string(), metricDetailSchema).optional().default({}),
  steps: z.array(stepSchema).min(1, "Au moins un step est requis"),
  initialState: z.object({
    agents: z.array(z.string()).default([]),
    agentTemplates: z.array(
      z.object({
      name: z.string().min(1),
      displayName: z.string().optional(),
      family: z.string().min(1),
      role: z.string().min(1),
      bio: z.string().optional(),
      quote: z.string().optional(),
      resources: z.record(
        z.string(), 
        z.coerce.number().min(0).max(100) // Transforme automatiquement "75" en 75
      ).optional().default({}),
      influence: z.record(z.string(), z.coerce.number().min(-1).max(1)).optional().default({}),
      theory: z.object({
        primaryConcept: z.string().optional(),
        author: z.string().optional(),
        year: z.coerce.number().int().optional(),
        quote: z.string().optional(),
        explanation: z.string().optional(),
      }).optional(),
      source: z.object({
        title: z.string().optional(),
        author: z.string().optional(),
        year: z.coerce.number().int().optional(),
        url: z.string().url().optional(),
      }).optional(),
      status: z.enum(['dominant', 'neutre', 'neutral', 'submissive', 'collaborative', 'influential', 'rebel', 'resistant', 'empathic', 'critical', 'influenced']).default('neutre'),
      allies: z.array(z.string()).default([]),
    })
    ).optional().default([]), 
    families: z.array(familySchema).min(1, "Au moins une famille requise"),
    theory: z.object({
      primaryConcept: z.string().optional(),
      secondaryConcept: z.string().optional(),
      author: z.string().optional(),
      year: optionalFlexibleYear, // Accepte "1950 / 2005" ou 1950
      quote: z.string().optional(),
      explanation: z.string().optional(),
      competingFrameworks: z.array(z.any()).optional(),
      }).optional(),
    source: z.object({
      title: z.string().optional(),
      author: z.string().optional(),
      year: optionalFlexibleYear, // Idem ici
      url: z.string().url().optional().or(z.literal("")),
      }).optional(),
    parameters: z.object({
      stabilite: z.coerce.number().default(50),
      creativite: z.coerce.number().default(50),
      equite: z.coerce.number().default(50),
      bienEtre: z.coerce.number().default(50),
      apprentissage: z.coerce.number().default(50),
      sante: z.coerce.number().default(50),
      mediation: z.coerce.number().default(50),
      emploi: z.coerce.number().default(50),
      }).optional(),
    interactions: z.array(z.any()).default([]),
    presets: z.array(z.object()).optional(),
  }).optional(),

  simulationEngine: z.object({
    computedMetrics: mapToObject.optional(),
    dynamics: z.array(dynamicSchema).optional(),
    causality: z.array(
      z.object({
        cause: z.string(),
        effect: z.string(),
        weight: z.coerce.number(),
        type: z.enum(['positive', 'negative']).optional(),
        evidence: z.object({
          source: z.string(),
          strength: z.coerce.number(),
        }).optional(),
      })
    ).optional(),
    quotes: z.array(z.string()).optional(),
    network: z.array(interactionSchema).optional(),
    predictionModel: z.object({
      enabled: z.boolean().default(true),
      method: z.enum(['regression', 'bayesian', 'markov', 'mlp', 'gradient']).default('bayesian'),
      horizon: z.coerce.number().default(15),
      counterfactuals: z.boolean().default(true),
    }).optional(),
    storeHistory: z.boolean().default(true),
    counterfactuals: z.boolean().default(true),
    calibration: z.object({
      enabled: z.boolean().default(true),
      mode: z.enum(['genetic', 'gradient', 'bayesian-optimization']).default('genetic'),
      targetMetrics: z.record(
        z.string(), 
        z.object({
          target: z.coerce.number(),
          weight: z.coerce.number()
        })
      ).optional(),
      populationSize: z.coerce.number().default(80),
      generations: z.coerce.number().default(60),
      mutationRate: z.coerce.number().default(0.15),
      eliteSize: z.coerce.number().default(12),
      learningRate: z.coerce.number().default(0.05),
      maxIterations: z.coerce.number().default(600),
      tolerance: z.coerce.number().default(0.005),
      autoStop: z.boolean().default(true),
    }).optional(),
    processMining: z.object({
      enabled: z.boolean().default(true),
      eventLog: z.array(z.object()).default([]),
      conformanceChecking: z.boolean().default(true),
      bottleneckDetection: z.boolean().default(true),
      discoveryAlgorithm: z.enum(['heuristic', 'inductive', 'alpha', 'genetic']).default('heuristic'),
      fitnessTarget: z.coerce.number().default(0.95),
    }).optional(),
    scenarioEngine: z.object({
      enabled: z.boolean().default(true),
      runs: z.coerce.number().default(500),
      variants: z.array(z.string()).optional(),
      storeDistributions: z.boolean().default(true),
    }).optional(),
    logs: z.object({
      store: z.boolean().default(true),
      granularity: z.enum(['action', 'tick', 'parameter', 'interaction']).default('tick'),
    }).optional(),
  }).optional(),

  processMap: z.object({
    nodes: z.array(
      z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        type: z.string().min(1),
      })
    ).optional(),
    edges: z.array(
      z.object({
        from: z.string().min(1),
        to: z.string().min(1),
      })
    ).optional(),
  }).optional(),

  processMapPro: z.object({
    nodes: z.array(
      z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        type: z.string().min(1),
      })
    ).optional(),
    edges: z.array(
      z.object({
        from: z.string().min(1),
        to: z.string().min(1),
      })
    ).optional(),
  }).optional(),

  optimizationGoals: z.object({
    primary: z.string().optional(),
    secondary: z.string().optional(),
    constraints: z.array(z.string()).optional(),
  }).optional(),

  optimalPath: z.object({
    actions: z.array(z.string()).optional(),
    predictedMetrics: mapToObject.optional(),
    probabilityOfSuccess: z.coerce.number().optional(),
    generationFound: z.coerce.number().optional(),
  }).optional(),

  counterfactuals: z.array(counterfactualSchema).optional(),

  currentTick: z.coerce.number().default(0).optional(),
  runtimeState: z.object().optional(),
  history: z.array(z.object()).default([]).optional(),
  
  sources: z.array(
    z.object({
      title: z.string().min(1),
      url: z.string().url().optional(),
      year: z.coerce.number().optional(),
      author: z.string().optional(),
      type: z.enum(['rapport', 'etude', 'article', 'donnees', 'schema', 'documentation']).default('rapport'),
    })
  ).optional(),

  solutions: z.array(solutionSchema).optional(),
  difficulty: z.enum(['facile', 'moyen', 'difficile', 'expert']).default('moyen'),
  metadata: z.object({
    hasProcessMining: z.boolean().default(true),
    hasAutoOptimization: z.boolean().default(true),
    hasCounterfactuals: z.boolean().default(true),
    hasDynamicDebrief: z.boolean().default(true),
    hasHiddenMetric: z.boolean().default(false),
    hasNarrativeOverride: z.boolean().default(true),
    hasOntologicalDebrief: z.boolean().default(false),
    versionEngine: z.string().default('7.0-pesa-autonome'),
    standalone: z.boolean().default(true),
    license: z.string().default('CC-BY-NC-SA-4.0'),
    author: z.string().default('© 2026 PESA · PESACORE Architect : Tous droits réservés. & PESA Corp'),
    createdAt: z.coerce.date().optional().default(() => new Date()),
  }).optional(),
  
  debriefConfig: z.object({
    showDelta: z.boolean().default(true),
    showRealWorldProbability: z.boolean().default(true),
    showKeyInsight: z.boolean().default(true),
    showHistoricalExamples: z.boolean().default(true),
    showPedagogicalMessage: z.boolean().default(true),
    language: z.string().default('fr'),
  }).optional(),

  estimatedTime: z.coerce.number().default(25),
  isPublished: z.boolean().default(false),
  isLegacy: z.boolean().default(false),
  version: z.coerce.number().default(5),

  createdBy: z.string().optional(), // ObjectId string
  orderInModule: z.coerce.number().default(0),
});

// Fonction utilitaire pour valider un seed JSON
export function validateGenericCase(data) {
  try {
    return genericCaseSchema.parse(data);
  } catch (error) {
    console.error("Validation Zod échouée :", error.errors);
    throw error;
  }
}

